import { useCallback, ReactElement, cloneElement, ComponentProps, ReactNode, useRef, KeyboardEvent } from 'react';
import classnames from 'classnames';

import { StaticDataFetcherItem } from '@hh.ru/magritte-common-data-provider';
import { keyboardKeys, keyboardMatches } from '@hh.ru/magritte-common-keyboard';
import { Button } from '@hh.ru/magritte-ui-button';
import { Cell, CellText } from '@hh.ru/magritte-ui-cell';
import { Drop } from '@hh.ru/magritte-ui-drop';
import { Loader } from '@hh.ru/magritte-ui-loader';
import { SelectError } from '@hh.ru/magritte-ui-select/SelectError';
import { SelectOption } from '@hh.ru/magritte-ui-select/SelectOption';
import { getChecked } from '@hh.ru/magritte-ui-select/getChecked';
import { OnChangeAction, SelectDropProps, RenderOptionTypeProps, SelectButtons } from '@hh.ru/magritte-ui-select/types';

import styles from './select-drop.less';

const SelectOptionDefault = <DataType extends StaticDataFetcherItem>({
    data,
    input,
    type,
    plain,
}: RenderOptionTypeProps<DataType>): ReactElement => {
    if (type === 'delimiter') {
        return (
            <CellText maxLines={1} style="secondary">
                {data.text}
            </CellText>
        );
    }
    if (type === 'label') {
        if (plain) {
            return <CellText maxLines={1}>{data.text}</CellText>;
        }
        return (
            <Cell Element="div" align="center">
                <div className={styles.labelOptionPlain}>
                    <CellText maxLines={1}>{data.text}</CellText>
                </div>
            </Cell>
        );
    }
    return (
        <Cell Element="div" align="center" left={input}>
            <CellText maxLines={1}>{data.text}</CellText>
        </Cell>
    );
};

const RenderButtonsContainerDefault = ({ applyChangesButton }: SelectButtons): ReactNode => {
    const primaryActions = [applyChangesButton].filter((btn) => btn);
    if (primaryActions.length === 0) {
        return null;
    }
    // заменить на action-bar, когда разберемся с отсутпами
    return <>{applyChangesButton}</>;
};

export const SelectDrop = <MultipleType extends boolean, DataType extends StaticDataFetcherItem>({
    activatorRef,
    type,
    name,
    multiple,
    applyChangesButton: _applyChangesButton,
    clearButton: _clearButton,
    useInitialValuesOnClearAction,
    loading,
    renderItem = SelectOptionDefault,
    renderButtonsContainer = RenderButtonsContainerDefault,
    visible,
    optionList,
    optionsListRef,
    searchValue,
    setSearchValue,
    maxWidth,
    value: values,
    onChange,
    onClose,
    clearSelectedValues,
    setInitialValues,
    isLimitExceeded,
    plain,
    searchable,
    widthEqualToActivator,
    forceDropVisibleWithEmptyOptions,
    renderContentBefore,
    setForcedState,
    setSelectedValues,
    maxHeight,
    onDropClose,
    onDropOpen,
    emptyOptionsHint,
    emptyOptionsSearchHint,
    onApply,
    onClear,
    searchInProgress,
    internalError,
    reload,
    innerErrorTrls,
    optionDataQa,
    ...props
}: SelectDropProps<MultipleType, DataType>): ReactElement => {
    const closedByKeyboardRef = useRef(false);
    const handleChange: OnChangeAction<DataType> = useCallback(
        (newValue, needToPut) => {
            onChange(newValue, needToPut);

            if (!multiple) {
                onClose?.();
            }

            if (!multiple && closedByKeyboardRef.current) {
                closedByKeyboardRef.current = false;
                setForcedState('focus');
                return;
            }

            if (multiple && searchValue) {
                setForcedState('focusAndSelect');
                return;
            }
            closedByKeyboardRef.current = false;
            setForcedState(null);
        },
        [multiple, onChange, onClose, searchValue, setForcedState]
    );

    const clearSelectedValuesAndClose = () => {
        if (useInitialValuesOnClearAction) {
            setInitialValues();
        } else {
            clearSelectedValues();
        }
        onClose?.();
    };
    const clearButton =
        _clearButton &&
        cloneElement(_clearButton, {
            key: 'clearButton',
            size: 'medium',
            'data-qa': 'magritte-select-clear-action',
            onClick: () => {
                clearSelectedValuesAndClose();
                onClear?.();
            },
        } as Partial<ComponentProps<typeof Button>>);
    const needAddChangesButton = multiple && _applyChangesButton;
    const applyChangesButton =
        (needAddChangesButton &&
            cloneElement(_applyChangesButton, {
                key: 'applyChangesButton',
                size: 'medium',
                'data-qa': 'magritte-select-apply',
                onClick: () => {
                    setSelectedValues();
                    onClose?.();
                    onApply?.({ isMobile: false, searchValue });
                },
            } as Partial<ComponentProps<typeof Button>>)) ||
        undefined;
    const footer =
        !loading &&
        needAddChangesButton &&
        renderButtonsContainer({
            applyChangesButton,
            clearButton,
        });

    const emptyOptionsHintVisible =
        optionList.length === 0 &&
        !searchValue &&
        !!emptyOptionsHint &&
        !loading &&
        !searchInProgress &&
        !internalError;
    const emptySearchOptionsHintVisible = !!(
        optionList.length === 0 &&
        searchValue &&
        !!emptyOptionsSearchHint &&
        !loading &&
        !searchInProgress &&
        !internalError
    );

    const optionsVisible = !loading && !emptySearchOptionsHintVisible && !emptyOptionsHintVisible && !internalError;

    return (
        <Drop
            {...props}
            ref={optionsListRef}
            autoFocusWhenOpened={false}
            maxWidth={maxWidth}
            visible={
                (optionList.length > 0 ||
                    forceDropVisibleWithEmptyOptions ||
                    emptyOptionsHintVisible ||
                    emptySearchOptionsHintVisible ||
                    internalError) &&
                visible
            }
            placement={['bottom-left', 'top-left']}
            activatorRef={activatorRef}
            role={searchable ? 'status' : 'listbox'}
            space={plain ? 600 : 300}
            keyboardNavigationMode="list"
            onClose={() => {
                setForcedState('focus');
                onClose?.();
            }}
            closeByClickOutside={false}
            onKeyDownCapture={(event: KeyboardEvent) => {
                if (keyboardMatches(event.nativeEvent, [keyboardKeys.Enter, keyboardKeys.Space])) {
                    closedByKeyboardRef.current = true;
                }
            }}
            widthEqualToActivator={widthEqualToActivator || loading}
            forcePlacement
            footer={footer}
            onAfterExit={onDropClose}
            onAppear={onDropOpen}
        >
            {internalError && innerErrorTrls && <SelectError innerErrorTrls={innerErrorTrls} reload={reload} />}
            {emptyOptionsHintVisible && <div className={styles.hint}>{emptyOptionsHint}</div>}
            {emptySearchOptionsHintVisible && <div className={styles.hint}>{emptyOptionsSearchHint}</div>}
            {loading && (
                <div className={styles.loading}>
                    <Loader size={24} />
                </div>
            )}
            {optionsVisible && (
                <>
                    {renderContentBefore && (
                        <div data-qa="magritte-select-header" className={styles.selectDropHeader}>
                            {renderContentBefore({ clearSelectedValuesAndClose, searchValue, setSearchValue })}
                        </div>
                    )}
                    <div
                        className={classnames(styles.selectDrop, { [styles.selectDropPlain]: plain })}
                        data-qa="magritte-select-option-list"
                        style={{ maxHeight: `${maxHeight}px` }}
                    >
                        {optionList.map((option) => {
                            const checked = Boolean(option.data && getChecked(option.data, values ?? []));
                            const disabled = !checked && (option.disabled || isLimitExceeded);
                            return (
                                <SelectOption
                                    key={option.data.value}
                                    name={name}
                                    checked={checked}
                                    onChange={handleChange}
                                    render={renderItem}
                                    disabled={disabled}
                                    type={option.type || type}
                                    plain={!!plain}
                                    isMobile={false}
                                    multiple={multiple}
                                    data-qa={optionDataQa}
                                    data={option.data}
                                />
                            );
                        })}
                    </div>
                </>
            )}
        </Drop>
    );
};
